/**
 * Componente para gestionar las entrevistas RH
 * Muestra lista de entrevistas y permite programar nuevas
 */
import { TituloDePantalla } from '@/shared/components/TituloDePantalla'

import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Calendar,
  Clock,
  MapPin,
  Video,
  Search,
  Eye,
  Star,
  FileText,
  XCircle
} from 'lucide-react'
import { Badge } from '@/shared/components/Badge'
import { hrInterviewService } from '@/features/rh/api/hrInterviewService'
import { useAuth } from '@/shared/hooks/useAuth'
import { nombreDeUsuario } from '@/shared/lib/nombreDeUsuario'
import { userHasPermission } from '@/shared/lib/roleUtils'
import type { HRInterview, HRInterviewFilters } from '@/types/hrInterview'
import { HRInterviewsCalendar } from '@/features/rh/components/hr-interviews/HRInterviewsCalendar'
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage'
import FilterSelect from '@/shared/components/FilterSelect'
import { EsqueletoTabla } from '@/shared/components/Esqueleto'
import { formatDate } from '@/shared/lib/dateUtils'

const HRInterviewsManagement: React.FC = () => {
  const { user, isLoading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [interviews, setInterviews] = useState<HRInterview[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  
  // Estado para controlar la vista activa (pestañas)
  const [activeView, setActiveView] = useState<'list' | 'dashboard'>('list')
  
  // Estados para filtros
  const [filters, setFilters] = useState<HRInterviewFilters>({})
  const [filtroPuesto, setFiltroPuesto] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    loadInterviews()
  }, [filters])

  const loadInterviews = async () => {
    setIsLoading(true)
    setError(null)
    
    try {
      const data = await hrInterviewService.getAllHRInterviews(filters)
      setInterviews(data)
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      console.error('Error al cargar entrevistas:', err)
      setError(err.message || 'Error al cargar las entrevistas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleViewCandidate = (candidateId: string) => {
    navigate(`/candidate/${candidateId}`)
  }

  const handleInterviewClick = (interview: HRInterview) => {
    navigate(`/candidate/${interview.candidate.id}`)
  }

  /**
   * Resultado de la entrevista, con el Badge compartido y sin ícono adentro:
   * en una tabla el ícono repite lo que ya dice la etiqueta.
   */
  const getStatusBadge = (interview: HRInterview) => {
    const config = {
      aprobado: { color: 'verde' as const, label: 'Aprobado por RH' },
      rechazado: { color: 'rojo' as const, label: 'Rechazado' },
      pendiente: { color: 'amarillo' as const, label: 'Pendiente' },
    }

    const { color, label } = config[interview.decision ?? 'pendiente']

    return <Badge color={color}>{label}</Badge>
  }

  /** Cada modalidad con su color, para distinguirlas de un vistazo. */
  const getModalityIcon = (modalidad: 'presencial' | 'virtual') =>
    modalidad === 'virtual' ? (
      <Video className="w-4 h-4 text-[#315FB9]" />
    ) : (
      <MapPin className="w-4 h-4 text-green-600" />
    )

  /**
   * El entrevistador puede no existir: si su cuenta se dio de baja, la
   * referencia se suelta y la entrevista se queda sin ella. Antes se leia
   * `firstName` directo y toda la pantalla se iba en blanco.
   */
  const nombreDelEntrevistador = (interview: HRInterview) =>
    nombreDeUsuario(interview.entrevistador ?? interview.entrevistadorHistorico)

  const puestoDe = (interview: HRInterview) =>
    interview.candidate.jobRequest?.puestoSolicitar ?? ''

  /** Puestos presentes en lo ya cargado, para no ofrecer opciones vacías. */
  const puestos = Array.from(
    new Set(interviews.map(puestoDe).filter(Boolean)),
  ).sort((a, b) => a.localeCompare(b, 'es'))

  const filteredInterviews = interviews.filter(interview => {
    const texto = searchTerm.trim().toLowerCase()
    const matchesSearch = texto === '' ||
      interview.candidate.fullName.toLowerCase().includes(texto) ||
      interview.candidate.email.toLowerCase().includes(texto) ||
      puestoDe(interview).toLowerCase().includes(texto) ||
      nombreDelEntrevistador(interview).toLowerCase().includes(texto)

    const matchesPuesto = filtroPuesto === '' || puestoDe(interview) === filtroPuesto

    return matchesSearch && matchesPuesto
  })

  // Mostrar loading mientras se verifica la autenticación
  if (authLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando entrevistas...</p>
        </div>
      </div>
    )
  }

  if (
    userHasPermission(
      "rh.entrevistas.base.read",
      user?.rbac?.effectivePermissions || []
    ) === false
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-8 mb-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <TituloDePantalla>
            Entrevistas de RH
          </TituloDePantalla>
          <p className="text-gray-600 mt-1">
            Administra las entrevistas de recursos humanos y sus resultados
          </p>
        </div>
        
        {/* Botones de pestañas - alineados a la derecha */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setActiveView('list')}
            className="p-2 rounded-lg transition-colors text-white hover:opacity-90"
            style={{ backgroundColor: activeView === 'list' ? '#253579' : '#FFFFFF', color: activeView === 'list' ? '#FFFFFF' : '#4B5563', border: activeView === 'list' ? 'none' : '1px solid #E5E7EB' }}
            title="Vista de Listado"
          >
            <FileText className="w-5 h-5" />
          </button>
          <button
            onClick={() => setActiveView('dashboard')}
            className="p-2 rounded-lg transition-colors text-white hover:opacity-90"
            style={{ backgroundColor: activeView === 'dashboard' ? '#253579' : '#FFFFFF', color: activeView === 'dashboard' ? '#FFFFFF' : '#4B5563', border: activeView === 'dashboard' ? 'none' : '1px solid #E5E7EB' }}
            title="Vista Dashboard"
          >
            <Calendar className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Contenido según la vista activa */}
      {activeView === 'list' ? (
        <>
          {/* Filters - Nueva barra de filtros visible */}
          {/* Barra de filtros con el mismo patrón de almacén: una sola fila
              blanca con borde, el buscador con lupa y los desplegables del
              componente compartido, en vez de selects nativos. */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex flex-wrap items-center gap-3 mb-6">
            <div className="relative w-full max-w-[320px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar en solicitud de vacantes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm placeholder-gray-400 focus:outline-none focus:border-[#315FB9]"
              />
            </div>

            <FilterSelect
              label="Tipo de entrevista"
              ancho="contenido"
              options={[
                { value: 'presencial', label: 'Presencial' },
                { value: 'virtual', label: 'Virtual' },
              ]}
              value={filters.modalidad ?? ''}
              onChange={(v) =>
                setFilters((prev) => {
                  const next = { ...prev }
                  if (v === '') delete next.modalidad
                  else next.modalidad = v as 'presencial' | 'virtual'
                  return next
                })
              }
            />

            {/* Puesto se filtra aquí y no en el servidor: el filtro del API no
                lo contempla, así que se recorta la lista ya cargada. */}
            <FilterSelect
              label="Puesto"
              ancho="contenido"
              options={puestos.map((p) => ({ value: p, label: p }))}
              value={filtroPuesto}
              onChange={setFiltroPuesto}
            />

            <FilterSelect
              label="Resultado"
              ancho="contenido"
              options={[
                { value: 'pendiente', label: 'Pendiente' },
                { value: 'aprobado', label: 'Aprobado por RH' },
                { value: 'rechazado', label: 'Rechazado' },
              ]}
              value={filters.decision ?? ''}
              onChange={(v) =>
                setFilters((prev) => {
                  const next = { ...prev }
                  if (v === '') delete next.decision
                  else next.decision = v as 'pendiente' | 'aprobado' | 'rechazado'
                  return next
                })
              }
            />

            <input
              type="date"
              value={filters.fechaDesde ?? ''}
              onChange={(e) =>
                setFilters((prev) => {
                  const next = { ...prev }
                  if (!e.target.value) delete next.fechaDesde
                  else next.fechaDesde = e.target.value
                  return next
                })
              }
              className="date-brand px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-[#315FB9]"
            />
            <input
              type="date"
              value={filters.fechaHasta ?? ''}
              onChange={(e) =>
                setFilters((prev) => {
                  const next = { ...prev }
                  if (!e.target.value) delete next.fechaHasta
                  else next.fechaHasta = e.target.value
                  return next
                })
              }
              className="date-brand px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-[#315FB9]"
            />

            <button
              type="button"
              onClick={() => {
                setFilters({})
                setSearchTerm('')
              }}
              className="text-sm font-semibold text-[#315FB9] hover:underline"
            >
              Limpiar
            </button>
          </div>

          {/* Content */}
          {error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <XCircle className="w-5 h-5 text-red-600 mr-2" />
                <p className="text-red-800">{error}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
              {isLoading ? (
                <EsqueletoTabla filas={6} columnas={9} />
              ) : filteredInterviews.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No hay entrevistas
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm || Object.keys(filters).length > 0
                      ? 'No se encontraron entrevistas con los filtros aplicados.'
                      : 'Aún no hay entrevistas RH programadas.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-[#E5E7EB]">
                    <thead className="bg-[#FAF9FF]">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Candidato
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Correo
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Fecha y hora
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Tipo de entrevista
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Entrevistador
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Puesto
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Resultado
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Evaluación
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-[#E1E1E1]">
                      {filteredInterviews.map((interview) => (
                        <tr key={interview.id} className="hover:bg-[#F4F7FB] transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            <button className="text-sm font-medium hover:underline transition-colors text-left">
                              {interview.candidate.fullName}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer text-sm text-gray-500"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            {interview.candidate.email}
                          </td>
                          {/* Fecha y hora en un solo renglón: apiladas partían
                              la fila en dos y la tabla quedaba desigual. */}
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            <div className="flex items-center gap-3 text-sm text-gray-900">
                              <span className="flex items-center gap-1.5">
                                <Calendar className="w-4 h-4 text-gray-400" />
                                {formatDate(interview.fechaEntrevista)}
                              </span>
                              <span className="flex items-center gap-1.5 text-gray-500">
                                <Clock className="w-4 h-4 text-gray-400" />
                                {interview.horaEntrevista}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            <div className="flex items-center">
                              {getModalityIcon(interview.modalidad)}
                              <span className="ml-2 text-sm text-gray-900 capitalize">
                                {interview.modalidad}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"   
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            <div className="flex items-center">
                              <span className="text-sm text-gray-900">
                                {nombreDelEntrevistador(interview)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer text-sm text-gray-900"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            {puestoDe(interview) || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            {getStatusBadge(interview)}
                          </td>
                          {/* La evaluación tiene columna propia: venía escondida
                              dentro de Acciones, después del botón Ver. */}
                          <td className="px-6 py-4 whitespace-nowrap cursor-pointer"
                            onClick={() => handleViewCandidate(interview.candidate.id)}
                          >
                            {interview.decision && interview.resultadoEntrevistaRh ? (
                              <span className="inline-flex items-center gap-1.5 text-sm text-gray-900">
                                <Star className="w-4 h-4 text-gray-400" />
                                {interview.resultadoEntrevistaRh}/10
                              </span>
                            ) : (
                              <span className="text-sm text-gray-400">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <button
                              onClick={() => handleViewCandidate(interview.candidate.id)}
                              className="text-blue-600 hover:text-blue-900 flex gap-2 items-center transition-colors"
                              title="Ver detalle del candidato"
                            >
                              <Eye className="w-4 h-4" />
                              Ver
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        /* Vista Dashboard - Calendario de Entrevistas */
        <HRInterviewsCalendar
          interviews={interviews}
          isLoading={isLoading}
          onInterviewClick={handleInterviewClick}
        />
      )}
    </div>
  )
}

export default HRInterviewsManagement
